"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSchedulePiece, usePublishNowPiece } from "@/hooks/use-calendar";
import { resolveTimeZone, instantToLocalParts, wallTimeToInstant } from "@content-os/scheduling";
import { MIN_SCHEDULE_LEAD_MS } from "@content-os/config";
import type { SocialPlatform } from "@content-os/config";
import type { SchedulePlatformInput } from "@content-os/types";
import { Calendar, Zap } from "lucide-react";

const pad = (n: number) => String(n).padStart(2, "0");

/** Format an instant as a `datetime-local` value ("YYYY-MM-DDTHH:mm") in `tz`. */
function toLocalInput(date: Date, tz: string): string {
  const p = instantToLocalParts(date, tz);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Parse a `datetime-local` value as a wall time in `tz` → ISO instant, or null
 *  when the value is empty / malformed / falls in a DST gap. */
function fromLocalInput(value: string, tz: string): string | null {
  const [d, t] = value.split("T");
  if (!d || !t) return null;
  const [y, mo, da] = d.split("-").map(Number);
  const [h, mi] = t.split(":").map(Number);
  if ([y, mo, da, h, mi].some(Number.isNaN)) return null;
  return wallTimeToInstant(y, mo, da, h, mi, tz)?.toISOString() ?? null;
}

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  pieceId: string;
  pieceCopy?: string;
  pieceCaption?: string;
  pieceHashtags?: string[];
  connectedPlatforms: SocialPlatform[];
  /** ISO instant to prefill every platform's time with (e.g. a calendar drop). */
  defaultScheduledFor?: string;
  /** Brand timezone for interpreting the datetime-local inputs. */
  tz?: string;
}

interface PlatformEntry {
  platform: SocialPlatform;
  enabled: boolean;
  scheduledFor: string;
  metadata: Record<string, string>;
  /** TikTok only: the user confirmed the preview and consented to publishing. */
  tiktokConsent: boolean;
}

/** Consent is only sent when given — a TikTok piece may already carry it in its knobs. */
function tiktokFields(e: PlatformEntry) {
  return e.platform === "TIKTOK" && e.tiktokConsent
    ? { tiktokSettings: { contentPreviewConfirmed: true, expressConsentGiven: true } }
    : {};
}

const DEFAULT_METADATA_FIELDS: Record<SocialPlatform, string[]> = {
  INSTAGRAM: ["caption", "hashtags", "firstComment", "altText"],
  TIKTOK: ["description", "hashtags"],
  FACEBOOK: ["postText"],
  TWITTER: ["tweetText"],
  LINKEDIN: ["postText"],
  YOUTUBE: ["title", "description", "tags"],
  THREADS: ["postText", "hashtags"],
  PINTEREST: ["title", "description"],
  REDDIT: ["title", "subreddit", "flairId"],
  GOOGLEBUSINESS: ["body", "callToAction"],
};

export function ScheduleModal({
  isOpen,
  onClose,
  brandId,
  pieceId,
  pieceCopy,
  pieceCaption,
  pieceHashtags,
  connectedPlatforms,
  defaultScheduledFor,
  tz,
}: ScheduleModalProps) {
  const { t } = useTranslation("calendar");
  const zone = resolveTimeZone(tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const scheduleMut = useSchedulePiece(brandId);
  const publishNowMut = usePublishNowPiece(brandId);
  const [activeTab, setActiveTab] = useState<SocialPlatform | null>(null);
  const [entries, setEntries] = useState<PlatformEntry[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    // Prefill with the dropped slot when provided, else tomorrow — in brand TZ.
    const base = defaultScheduledFor
      ? new Date(defaultScheduledFor)
      : new Date(Date.now() + 86400_000);
    const defaultTime = toLocalInput(base, zone);
    const initial = connectedPlatforms.map((platform) => ({
      platform,
      enabled: false,
      scheduledFor: defaultTime,
      metadata: getDefaultMetadata(platform, pieceCopy, pieceCaption, pieceHashtags),
      tiktokConsent: false,
    }));
    setEntries(initial);
    setActiveTab(connectedPlatforms[0] ?? null);
  }, [isOpen, connectedPlatforms, pieceCopy, pieceCaption, pieceHashtags, defaultScheduledFor, zone]);

  const togglePlatform = (platform: SocialPlatform) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.platform === platform ? { ...e, enabled: !e.enabled } : e,
      ),
    );
  };

  const updateMetadata = (
    platform: SocialPlatform,
    field: string,
    value: string,
  ) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.platform === platform
          ? { ...e, metadata: { ...e.metadata, [field]: value } }
          : e,
      ),
    );
  };

  const updateTiktokConsent = (platform: SocialPlatform, tiktokConsent: boolean) => {
    setEntries((prev) =>
      prev.map((e) => (e.platform === platform ? { ...e, tiktokConsent } : e)),
    );
  };

  const updateTime = (platform: SocialPlatform, time: string) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.platform === platform ? { ...e, scheduledFor: time } : e,
      ),
    );
  };

  const handleSchedule = () => {
    const platforms: SchedulePlatformInput[] = [];
    for (const e of entries) {
      if (!e.enabled) continue;
      const scheduledFor = fromLocalInput(e.scheduledFor, zone);
      if (!scheduledFor) return; // a blank/invalid time — abort rather than crash
      platforms.push({ platform: e.platform, scheduledFor, metadata: e.metadata, ...tiktokFields(e) });
    }

    if (platforms.length === 0) return;

    scheduleMut.mutate({ pieceId, platforms }, { onSuccess: onClose });
  };

  const handlePublishNow = () => {
    const platforms = entries
      .filter((e) => e.enabled)
      .map((e) => ({ platform: e.platform, metadata: e.metadata, ...tiktokFields(e) }));

    if (platforms.length === 0) return;
    if (!confirm(t("scheduleModal.publishConfirm"))) return;

    publishNowMut.mutate({ pieceId, platforms }, { onSuccess: onClose });
  };

  const activeEntry = entries.find((e) => e.platform === activeTab);
  const enabledCount = entries.filter((e) => e.enabled).length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar size={18} />
            {t("scheduleModal.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Platform selection */}
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase mb-2">
              {t("scheduleModal.selectPlatforms")}
            </div>
            <div className="flex flex-wrap gap-2">
              {entries.map((entry) => (
                <label
                  key={entry.platform}
                  className="flex items-center gap-1.5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={entry.enabled}
                    onChange={() => togglePlatform(entry.platform)}
                    className="h-4 w-4 rounded border border-input accent-primary cursor-pointer"
                  />
                  <span className="text-sm">
                    {t(`platform.${entry.platform}`, { defaultValue: entry.platform.charAt(0) + entry.platform.slice(1).toLowerCase() })}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Platform tabs */}
          {enabledCount > 0 && (
            <>
              <div className="flex gap-1 border-b">
                {entries
                  .filter((e) => e.enabled)
                  .map((e) => (
                    <button
                      key={e.platform}
                      onClick={() => setActiveTab(e.platform)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs border-b-2 ${
                        activeTab === e.platform
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground"
                      }`}
                    >
                      {t(`platform.${e.platform}`, { defaultValue: e.platform.charAt(0) + e.platform.slice(1).toLowerCase() })}
                    </button>
                  ))}
              </div>

              {activeEntry && (
                <div className="space-y-3">
                  {(DEFAULT_METADATA_FIELDS[activeEntry.platform] ?? []).map(
                    (field) => (
                      <div key={field}>
                        <label className="text-xs font-medium capitalize text-muted-foreground">
                          {t(`scheduleModal.fields.${field}`, { defaultValue: field.replace(/([A-Z])/g, " $1") })}
                        </label>
                        {field.includes("Text") ||
                        field === "caption" ||
                        field === "description" ? (
                          <Textarea
                            value={activeEntry.metadata[field] ?? ""}
                            onChange={(e) =>
                              updateMetadata(
                                activeEntry.platform,
                                field,
                                e.target.value,
                              )
                            }
                            className="mt-1 h-20"
                            maxLength={
                              field === "tweetText" ? 280 : undefined
                            }
                          />
                        ) : (
                          <Input
                            value={activeEntry.metadata[field] ?? ""}
                            onChange={(e) =>
                              updateMetadata(
                                activeEntry.platform,
                                field,
                                e.target.value,
                              )
                            }
                            className="mt-1"
                          />
                        )}
                      </div>
                    ),
                  )}

                  {activeEntry.platform === "TIKTOK" && (
                    <label className="flex items-start gap-2 text-xs">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={activeEntry.tiktokConsent}
                        onChange={(e) =>
                          updateTiktokConsent(activeEntry.platform, e.target.checked)
                        }
                      />
                      <span>{t("scheduleModal.tiktokConsent")}</span>
                    </label>
                  )}

                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      {t("scheduleModal.scheduleFor")}
                    </label>
                    <Input
                      type="datetime-local"
                      value={activeEntry.scheduledFor}
                      onChange={(e) =>
                        updateTime(activeEntry.platform, e.target.value)
                      }
                      min={toLocalInput(new Date(Date.now() + MIN_SCHEDULE_LEAD_MS), zone)}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose}>
              {t("scheduleModal.cancel")}
            </Button>
            <Button
              variant="outline"
              onClick={handlePublishNow}
              disabled={enabledCount === 0 || publishNowMut.isPending || scheduleMut.isPending}
              title={t("scheduleModal.publishNowTitle")}
            >
              <Zap size={14} className="mr-1" />
              {publishNowMut.isPending ? t("scheduleModal.publishing") : t("scheduleModal.publishNow")}
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={enabledCount === 0 || scheduleMut.isPending || publishNowMut.isPending}
            >
              {t("scheduleModal.schedule", { count: enabledCount })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getDefaultMetadata(
  platform: SocialPlatform,
  copy?: string,
  caption?: string,
  hashtags?: string[],
): Record<string, string> {
  const text = caption || copy || "";
  const tags = hashtags?.join(" ") ?? "";

  switch (platform) {
    case "INSTAGRAM":
      return { caption: text, hashtags: tags, firstComment: "", altText: "" };
    case "TIKTOK":
      return { description: text, hashtags: tags };
    case "FACEBOOK":
      return { postText: text };
    case "TWITTER":
      return { tweetText: text.slice(0, 280) };
    case "LINKEDIN":
      return { postText: text };
    case "YOUTUBE":
      return { title: text.slice(0, 100), description: text, tags };
    case "THREADS":
      return { postText: text, hashtags: tags };
    case "PINTEREST":
      return { title: text.slice(0, 100), description: text };
    default:
      return {};
  }
}
